import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Callout } from "../../ui";
import { UnitSystem } from "../UnitSystem";
import { motion, AnimatePresence } from "framer-motion";
import usePrice from "../../../hooks/usePrice";

// Updated shipping rate structure to match new API response
export interface ShippingRate {
  id: string;
  provider: string;
  provider_display_name: string;
  service_id: string;
  name: string;
  description: string;
  price: number;
  base_price: number;
  has_commission: boolean;
  currency: string;
  original_currency: string;
  transit_days: number;
  estimated_delivery_date: string;
  carrier_name: string;
  service_type: string;
  transit_time: string;
  insurance_up_to: string;
  printing_required: boolean;
  signature_required: boolean;
  doorstep_delivery: boolean;
  online_tracking: boolean;
  price_business_delivery: { amount: number; currency: string };
  price_private_delivery: { amount: number; currency: string };
}

interface ShippingRatesProps {
  visible: boolean;
  rates: ShippingRate[];
  selectedRateId: string | null;
  currency: string;
  isRTL: boolean;
  totalWeight: number;
  unitSystem: UnitSystem;
  hideSelectButton?: boolean;
  onRateSelect: (rateId: string) => void;
  onContinue: () => void;
  onBack?: () => void;
  showSortOptions?: boolean;
  onRefresh?: () => void;
  error?: ShippingError | null;
  scrollToTopWhenSelected?: boolean;
}

interface ShippingError {
  success: boolean;
  providers?: string[];
  provider?: string;
  provider_description?: string;
  error_message: string | null;
}

type SortOption = "price" | "delivery" | "carrier";
type SortDirection = "asc" | "desc";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 15 } },
};

const formatDeliveryDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

interface ServiceFeature {
  name: string;
  icon: string;
  description: string;
  category: "delivery" | "requirement" | "service" | "security";
}

const getServiceFeatures = (rate: ShippingRate): ServiceFeature[] => {
  const features: ServiceFeature[] = [];
  if (rate.doorstep_delivery)
    features.push({ name: "Doorstep Delivery", icon: "🏠", description: "Delivered directly to your doorstep", category: "delivery" });
  if (rate.online_tracking)
    features.push({ name: "Real-time Tracking", icon: "📍", description: "Track your package in real-time", category: "service" });
  if (rate.signature_required)
    features.push({ name: "Signature Required", icon: "✍️", description: "Recipient signature required on delivery", category: "security" });
  if (rate.printing_required)
    features.push({ name: "Label Printing Required", icon: "🖨️", description: "You must print the shipping label", category: "requirement" });
  if (rate.insurance_up_to)
    features.push({ name: `Insured up to ${rate.insurance_up_to}`, icon: "🛡️", description: `Package insured up to ${rate.insurance_up_to}`, category: "security" });
  return features;
};

const categoryColors: Record<string, { bg: string; color: string; border: string }> = {
  delivery:    { bg: "rgba(15,163,114,0.08)",  color: "var(--ok)",         border: "rgba(15,163,114,0.2)" },
  service:     { bg: "rgba(43,85,255,0.08)",   color: "var(--brand-blue)", border: "rgba(43,85,255,0.2)" },
  security:    { bg: "rgba(43,85,255,0.08)",   color: "var(--brand-blue)", border: "rgba(43,85,255,0.2)" },
  requirement: { bg: "rgba(245,158,11,0.08)",  color: "#b45309",           border: "rgba(245,158,11,0.2)" },
};

// RateCard sub-component
const RateCard = React.memo(({
  rate,
  index,
  isSelected,
  onSelect,
  onContinue,
  scrollToTopWhenSelected,
}: {
  rate: ShippingRate;
  index: number;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onContinue: () => void;
  scrollToTopWhenSelected: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleContinueClick = useCallback(
    (e?: React.MouseEvent) => { if (e) e.stopPropagation(); onContinue(); },
    [onContinue]
  );

  const handleClick = useCallback(() => {
    onSelect(rate.id);
    handleContinueClick();
  }, [rate.id, onSelect, handleContinueClick]);

  const handleExpandClick = useCallback(
    (e: React.MouseEvent) => { e.stopPropagation(); setIsExpanded((v) => !v); },
    []
  );

  const price = usePrice({ amount: rate.price, currencyCode: rate.currency });
  const features = getServiceFeatures(rate);
  const topFeatures = [
    ...(rate.doorstep_delivery ? ["Doorstep Delivery"] : []),
    ...(rate.online_tracking   ? ["Tracking"]          : []),
    ...(rate.insurance_up_to   ? ["Insured"]            : []),
  ].slice(0, 2);

  return (
    <motion.div ref={cardRef} variants={itemVariants} custom={index} style={{ marginBottom: 12 }}>
      <div
        onClick={handleClick}
        style={{
          border: `${isSelected ? 2 : 1}px solid ${isSelected ? "var(--brand-blue)" : "var(--line)"}`,
          borderRadius: 10,
          cursor: "pointer",
          background: isSelected ? "rgba(43,85,255,0.04)" : "var(--bg-elev)",
          boxShadow: isSelected ? "0 6px 20px rgba(0,0,0,0.12)" : "0 2px 8px rgba(0,0,0,0.06)",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.boxShadow = isSelected ? "0 8px 24px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.1)";
          el.style.transform = isSelected ? "translateY(-4px)" : "translateY(-2px)";
          el.style.borderColor = "var(--brand-blue)";
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.boxShadow = isSelected ? "0 6px 20px rgba(0,0,0,0.12)" : "0 2px 8px rgba(0,0,0,0.06)";
          el.style.transform = "none";
          el.style.borderColor = isSelected ? "var(--brand-blue)" : "var(--line)";
        }}
      >
        {/* Selected indicator bar */}
        {isSelected && (
          <div
            style={{
              position: "absolute", top: 0, left: 0, width: 4,
              height: "100%", background: "var(--brand-blue)",
            }}
          />
        )}

        <div style={{ padding: "16px 20px 16px 24px" }}>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            {/* Radio indicator */}
            <div
              onClick={handleClick}
              role="radio"
              aria-checked={isSelected}
              style={{
                width: 20, height: 20, borderRadius: "50%",
                border: `2px solid ${isSelected ? "var(--brand-blue)" : "var(--line)"}`,
                background: isSelected ? "var(--brand-blue)" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginTop: 2, marginRight: 14, flexShrink: 0,
                cursor: "pointer", transition: "all 0.2s ease", position: "relative",
              }}
            >
              {isSelected && (
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", position: "absolute" }} />
              )}
            </div>

            {/* Main content */}
            <div style={{ flex: 1 }}>
              {/* Header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ flex: 1, marginRight: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2, color: isSelected ? "var(--brand-blue)" : "var(--ink)" }}>
                    {rate.provider_display_name}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{rate.name}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, fontSize: 18, color: "var(--brand-blue)" }}>
                    {price.priceWithSymbol}
                  </div>
                </div>
              </div>

              {/* Quick info row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <span style={{ fontSize: 12, color: "var(--ink-3)" }}>⏱ {rate.transit_time}</span>
                  <span style={{ fontSize: 12, color: "var(--ink-3)" }}>📅 {formatDeliveryDate(rate.estimated_delivery_date)}</span>
                </div>
                <button
                  type="button"
                  onClick={handleExpandClick}
                  title={isExpanded ? "Show less" : "Show details"}
                  style={{
                    width: 28, height: 28, borderRadius: "50%", border: "none",
                    background: "rgba(43,85,255,0.08)", cursor: "pointer", fontSize: 12,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(43,85,255,0.15)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(43,85,255,0.08)"; }}
                >
                  {isExpanded ? "▲" : "▼"}
                </button>
              </div>

              {/* Quick feature chips */}
              {!isExpanded && topFeatures.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {topFeatures.map((f, i) => (
                    <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8, background: "rgba(15,163,114,0.08)", color: "var(--ok)", border: "1px solid rgba(15,163,114,0.2)" }}>
                      {f}
                    </span>
                  ))}
                  {features.length > topFeatures.length && (
                    <button type="button" onClick={handleExpandClick} style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8, background: "rgba(43,85,255,0.08)", color: "var(--brand-blue)", border: "1px solid rgba(43,85,255,0.2)", cursor: "pointer" }}>
                      +{features.length - topFeatures.length} more
                    </button>
                  )}
                </div>
              )}

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ marginTop: 14 }}>
                  <hr style={{ border: "none", borderTop: "1px solid var(--line)", margin: "0 0 14px" }} />

                  {rate.description && rate.description !== rate.name && (
                    <p style={{ fontSize: 13, fontStyle: "italic", color: "var(--ink-3)", padding: "10px 14px", background: "rgba(43,85,255,0.03)", borderRadius: 6, border: "1px solid rgba(43,85,255,0.1)", marginBottom: 14 }}>
                      {rate.description}
                    </p>
                  )}

                  {/* Delivery details */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                      Delivery Details
                    </div>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                      {[
                        { icon: "⏱", value: rate.transit_time, label: "Transit time" },
                        { icon: "📅", value: formatDeliveryDate(rate.estimated_delivery_date), label: "Expected delivery" },
                      ].map(({ icon, value, label }) => (
                        <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 16 }}>{icon}</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{value}</div>
                            <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Feature groups */}
                  {(["delivery", "service", "security", "requirement"] as const).map((cat) => {
                    const catFeatures = features.filter((f) => f.category === cat);
                    if (catFeatures.length === 0) return null;
                    const labels: Record<string, string> = { delivery: "Service Features", service: "Service Features", security: "Security & Insurance", requirement: "Requirements" };
                    const colors = categoryColors[cat];
                    return (
                      <div key={cat} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
                          {labels[cat]}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {catFeatures.map((feature, i) => (
                            <span key={i} title={feature.description} style={{ fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 10, background: colors.bg, color: colors.color, border: `1px solid ${colors.border}`, cursor: "help" }}>
                              {feature.icon} {feature.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

RateCard.displayName = "RateCard";

const ShippingRates: React.FC<ShippingRatesProps> = ({
  visible,
  rates,
  selectedRateId,
  currency,
  isRTL,
  totalWeight,
  unitSystem,
  onRateSelect,
  onContinue,
  onBack,
  showSortOptions,
  hideSelectButton,
  onRefresh,
  error,
  scrollToTopWhenSelected = false,
}) => {
  const [sortBy, setSortBy] = useState<SortOption>("price");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [errorData, setErrorData] = useState<ShippingError | null>(null);

  useEffect(() => { setErrorData(error || null); }, [error]);

  const handleRateSelect = useCallback((id: string) => onRateSelect(id), [onRateSelect]);

  const handleSortDirectionToggle = useCallback(() => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  }, []);

  const handleSortByChange = useCallback((option: SortOption) => setSortBy(option), []);

  const sortedRates = useMemo(() => {
    if (!rates || rates.length === 0) return [];
    return [...rates].sort((a, b) => {
      switch (sortBy) {
        case "price":    return sortDirection === "asc" ? a.price - b.price : b.price - a.price;
        case "delivery": return sortDirection === "asc" ? a.transit_days - b.transit_days : b.transit_days - a.transit_days;
        case "carrier":  return sortDirection === "asc" ? a.provider_display_name.localeCompare(b.provider_display_name) : b.provider_display_name.localeCompare(a.provider_display_name);
        default:         return 0;
      }
    });
  }, [rates, sortBy, sortDirection]);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <div>
          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              {onBack && (
                <Button variant="secondary" onClick={onBack} style={{ fontSize: 13, padding: "5px 12px" }}>
                  ← Back to Search
                </Button>
              )}
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Available Shipping Options</h2>
            </div>
            <p style={{ margin: 0, fontSize: 14, color: "var(--ink-3)" }}>
              Compare shipping services and select your preferred delivery method
            </p>
          </div>

          {/* Error */}
          {errorData && (
            <div style={{ marginBottom: 20 }}>
              <Callout tone="warn">
                <div>
                  <div>{errorData.error_message || "Failed to fetch shipping rates. Please try again."}</div>
                  {errorData.provider && <div style={{ marginTop: 4, fontSize: 13 }}>Provider: {errorData.provider}</div>}
                  {errorData.provider_description && <div style={{ marginTop: 2, fontSize: 12, color: "var(--ink-4)" }}>{errorData.provider_description}</div>}
                  {onRefresh && (
                    <button type="button" onClick={onRefresh} style={{ marginTop: 8, padding: "4px 12px", fontSize: 12, border: "1px solid currentColor", borderRadius: 4, background: "transparent", cursor: "pointer", color: "inherit" }}>
                      Retry
                    </button>
                  )}
                </div>
              </Callout>
            </div>
          )}

          {/* No rates */}
          {!errorData && (!rates || rates.length === 0) && (
            <div style={{ marginBottom: 20 }}>
              <Callout tone="info">
                No shipping rates available. Please try different shipping details or click "Get Quotes" to fetch rates.
              </Callout>
            </div>
          )}

          {/* Sort options */}
          {showSortOptions && sortedRates.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: 20, padding: "12px 16px", background: "rgba(43,85,255,0.04)", borderRadius: 8, gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-3)" }}>Sort by:</span>
                <div style={{ display: "flex", border: "1px solid var(--line)", borderRadius: 6, overflow: "hidden" }}>
                  {(["price", "delivery", "carrier"] as SortOption[]).map((opt) => {
                    const labels = { price: "💰 Price", delivery: "⏱ Delivery", carrier: "🚚 Carrier" };
                    const active = sortBy === opt;
                    return (
                      <button key={opt} type="button" onClick={() => handleSortByChange(opt)} style={{ padding: "6px 14px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400, background: active ? "var(--brand-blue)" : "var(--bg-elev)", color: active ? "#fff" : "var(--ink-3)", transition: "all 0.15s" }}>
                        {labels[opt]}
                      </button>
                    );
                  })}
                </div>
                <button type="button" onClick={handleSortDirectionToggle} title={sortDirection === "asc" ? "Ascending" : "Descending"} style={{ width: 32, height: 32, borderRadius: 6, border: "1px solid var(--line)", background: "var(--bg-elev)", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {sortDirection === "asc" ? "↑" : "↓"}
                </button>
              </div>
              <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
                {sortedRates.length} option{sortedRates.length !== 1 ? "s" : ""} available
              </span>
            </div>
          )}

          {/* Rate cards */}
          {!errorData && sortedRates.length > 0 && (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <div style={{ marginBottom: 20 }}>
                {sortedRates.map((rate, index) => (
                  <RateCard
                    key={rate.id}
                    rate={rate}
                    index={index}
                    isSelected={rate.id === selectedRateId}
                    onSelect={handleRateSelect}
                    onContinue={onContinue}
                    scrollToTopWhenSelected={scrollToTopWhenSelected}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Continue button */}
          {!errorData && sortedRates.length > 0 && !hideSelectButton && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
              <Button
                variant="blue"
                disabled={!selectedRateId}
                onClick={() => {
                  if (scrollToTopWhenSelected) window.scrollTo({ top: 0, behavior: "smooth" });
                  onContinue();
                }}
                style={{ padding: "12px 32px", fontSize: 15, fontWeight: 600 }}
              >
                Continue with Selected Option
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default React.memo(ShippingRates);
